export const eventsEn = {
  tabs: {
    ariaLabel: "Journal view",
    events: "Events",
    medications: "Medications",
    doctorVisits: "Doctor Visits",
  },
  medications: {
    title: "Medications",
    intro: "Track ongoing medications, adjust doses, and log adherence notes in one place.",
    placeholder: "Your medications will appear here. This feature is coming soon.",
    addMedication: "New Medication",
    // Medication card fields
    dose: "Dose",
    route: "Route",
    activeSince: "Active since {{date}}",
    stoppedOn: "Stopped {{date}}",
    activeStatus: "Active",
    stoppedStatus: "Stopped",
    showInTrends: "Show on chart",
    regularInfo: "Regular/ongoing medications belong here. One-off pain meds can be logged in daily check-ins.",
    regularInfoAria: "Show regular medication guidance",
    noMedications: "No medications yet",
    noMedicationsHint: "Medications you start will appear here.",
    loading: "Loading medications…",
    errorLoading: "Could not load medications",
    retry: "Retry",
    // Card actions
    actions: {
      edit: "Edit",
      switch: "Switch",
      stop: "Stop",
      logNote: "Log adherence note",
      delete: "Delete",
    },
    // Edit dialog
    editDialog: {
      title: "Edit Medication",
      displayName: "Name",
      doseValue: "Dose value",
      doseValuePlaceholder: "e.g., 200",
      doseUnit: "Dose unit",
      doseUnitPlaceholder: "e.g., mg",
      route: "Route",
      routePlaceholder: "e.g., oral",
      save: "Save changes",
      saving: "Saving…",
      cancel: "Cancel",
    },
    // Stop dialog
    stopDialog: {
      title: "Stop Medication",
      description: "Set the date this medication was stopped.",
      dateLabel: "Stop date",
      confirm: "Stop medication",
      confirming: "Stopping…",
      cancel: "Cancel",
    },
    // Switch dialog
    switchDialog: {
      title: "Switch Medication",
      description: "Switch from {{from}} to a new medication.",
      dateLabel: "Switch date",
      toLabel: "To (new medication)",
      toPlaceholder: "Search or type medication name…",
      toRequired: "Please enter the new medication name",
      notesLabel: "Notes (optional)",
      notesPlaceholder: "Any notes about the switch…",
      confirm: "Switch medication",
      confirming: "Switching…",
      cancel: "Cancel",
    },
    // Adherence note
    noteDialog: {
      title: "Log Adherence Note",
      description: "Record a note about taking this medication.",
      dateLabel: "Date",
      notePlaceholder: "e.g., Missed morning dose due to travel",
      submit: "Save note",
      submitting: "Saving…",
      cancel: "Cancel",
    },
    // Delete dialog (T5.4)
    deleteDialog: {
      title: "Delete Medication",
      description: "This will hide {{name}} from all medication lists. Historical events will be kept.",
      confirm: "Delete medication",
      confirming: "Deleting…",
      cancel: "Cancel",
    },
    // Add medication dialog
    addDialog: {
      title: "Add Medication",
      nameLabel: "Medication name",
      namePlaceholder: "Search or type medication name…",
      nameRequired: "Please enter a medication name",
      doseValueLabel: "Dose value",
      doseValuePlaceholder: "e.g., 200",
      doseUnitLabel: "Dose unit",
      doseUnitPlaceholder: "e.g., mg",
      startDateLabel: "Start date",
      startDateRequired: "Please enter a start date",
      notesLabel: "Notes (optional)",
      notesPlaceholder: "Any notes about this medication…",
      showInTrends: "Show on chart",
      save: "Add medication",
      saving: "Adding…",
      cancel: "Cancel",
    },
  },
  doctorVisits: {
    title: "Doctor Visits",
    intro: "Plan upcoming visits and capture talking points, questions, and concerns for each appointment.",
    placeholder: "Plan and prepare for doctor visits. Upcoming and past visits will appear here.",
    // List view
    upcoming: "Upcoming",
    past: "Past",
    noVisits: "No doctor visits yet",
    noVisitsHint: "Add a visit to start preparing your talking points.",
    addVisit: "New Visit",
    loading: "Loading visits…",
    errorLoading: "Could not load visits",
    retry: "Retry",
    // Card
    itemCount_one: "{{count}} talking point",
    itemCount_other: "{{count}} talking points",
    statusPlanned: "Planned",
    statusCompleted: "Completed",
    statusCancelled: "Cancelled",
    // Detail view
    back: "Back to visits",
    visitDate: "Date",
    visitTime: "Time",
    visitType: "Visit type",
    clinician: "Clinician",
    clinic: "Clinic",
    status: "Status",
    agendaNotes: "Agenda notes",
    talkingPoints: "Talking Points",
    noItems: "No talking points yet",
    noItemsHint: "Add questions, concerns, or topics to discuss at your visit.",
    addItem: "Add talking point",
    // Item types
    itemType: {
      question: "Question",
      concern: "Concern",
      symptom_change: "Symptom Change",
      medication: "Medication",
      event: "Event",
      functional_impact: "Functional Impact",
      other: "Other",
    },
    // Create visit dialog
    createTitle: "New Doctor Visit",
    createVisitDate: "Visit date",
    createVisitType: "Visit type",
    createVisitTime: "Time (optional)",
    createClinician: "Clinician (optional)",
    createClinic: "Clinic (optional)",
    createSave: "Create visit",
    createSaving: "Creating…",
    createCancel: "Cancel",
    // Add item dialog
    addItemTitle: "Add Talking Point",
    addItemText: "What do you want to discuss?",
    addItemTextPlaceholder: "e.g., Ask about switching to extended-release…",
    addItemType: "Type",
    addItemSave: "Add",
    addItemSaving: "Adding…",
    addItemCancel: "Cancel",
    // Visit types
    visitTypes: {
      general_checkup: "General Checkup",
      specialist: "Specialist",
      follow_up: "Follow-up",
      urgent_care: "Urgent Care",
      other: "Other",
    },
    // Prepare packet wizard (T5.3)
    wizard: {
      title: "Prepare Visit Packet",
      close: "Close",
      back: "Back",
      next: "Next",
      stepOf: "Step {{current}} of {{total}}",
      // Step 1: Your Words
      step1Title: "Your Words",
      step1Description: "Jot down anything you want to mention at your visit. This is optional — you can skip ahead.",
      notesPlaceholder: "e.g., I've been feeling more stiffness in the mornings, especially after…",
      aiDraftButton: "Draft with AI",
      aiComingSoon: "Coming soon",
      skipStep: "Skip",
      // Step 2: Timeframe
      step2Title: "Timeframe",
      step2Description: "Choose the date range for your summary data.",
      timeframePresets: {
        sinceLastVisit: "Since last visit",
        last2Weeks: "Last 2 weeks",
        last30Days: "Last 30 days",
        last90Days: "Last 90 days",
        custom: "Custom",
      },
      timeframeBasis: {
        last_completed_visit_by_type_and_clinician: "Based on your last visit with this clinician",
        last_completed_visit_by_type: "Based on your last {{visitType}} visit",
        last_completed_visit_global: "Based on your most recent completed visit",
        default_30_days: "Default: last 30 days",
      },
      customStart: "Start date",
      customEnd: "End date",
      // Step 3: Include & Preview
      step3Title: "Include & Preview",
      step3Description: "Choose what to include in your packet and review the highlights.",
      sections: {
        painSummary: "Pain Summary",
        medications: "Medications",
        functionalImpact: "Functional Impact",
        keyEvents: "Key Events",
        appendix: "Appendix (daily details)",
      },
      loadingContext: "Loading your data…",
      errorContext: "Could not load data for this range.",
      retryContext: "Retry",
      noData: "No data available for this date range.",
      painStatsLabel: "Pain Statistics",
      painMean: "Average",
      painMedian: "Median",
      painMin: "Lowest",
      painMax: "Highest",
      daysTracked_one: "{{count}} day tracked",
      daysTracked_other: "{{count}} days tracked",
      topLocations: "Top Locations",
      topQualities: "Top Qualities",
      medicationChanges: "Medication Changes",
      medicationChangeStarted: "Started",
      medicationChangeStopped: "Stopped",
      medicationChangeDose: "Dose change",
      noMedicationChanges: "No medication changes in this period",
      functionalImpactAvg: "Average impact",
      noFunctionalData: "No functional impact data recorded",
      suggestedEvents: "Suggested Key Events",
      noEvents: "No key events in this period",
      // Step 4: Review
      step4Title: "Review Summary",
      step4Description: "Review your packet before exporting. Toggle privacy options below.",
      omitNotes: "Omit personal notes",
      removePatientName: "Remove patient name",
      reviewSection: {
        painSummary: "Pain Summary",
        medications: "Medications",
        functionalImpact: "Functional Impact",
        keyEvents: "Key Events",
      },
      // Step 5: Export
      step5Title: "Export",
      step5Description: "Download your packet or copy it as text.",
      exportPdf: "Download PDF",
      exportText: "Download Text",
      exporting: "Exporting…",
      exportSuccess: "Downloaded!",
      exportError: "Export failed. Please try again.",
      // AI draft review (T4.2)
      aiProgress: "{{message}}",
      aiReviewTitle: "Review AI Draft",
      aiReviewDescription: "Review the suggested visit and talking points. Accept, edit, or reject each item.",
      aiVisitHeader: "Visit Details",
      aiAcceptAll: "Accept All",
      aiConfirm: "Save Accepted Items",
      aiConfirming: "Saving...",
      aiReject: "Reject",
      aiAccept: "Accept",
      aiConfidence: "Confidence: {{value}}%",
      aiSource: "Source: {{source}}",
      aiSourceLabel: {
        ai_draft: "AI draft",
        user: "You",
      },
      aiSourceRefLabel: {
        event: "From an event",
        daily_record: "From daily record",
        medication: "From a medication",
        freeform: "From your text",
      },
      aiNoItems: "No items were drafted.",
      aiError: "AI drafting failed. Please try again.",
      aiItemCount_one: "{{count}} item drafted",
      aiItemCount_other: "{{count}} items drafted",
    },
    preparePacket: "Prepare Packet",
    // Add with AI dialog (T4.3)
    addWithAi: {
      button: "Add with AI",
      title: "Add with AI",
      subtitle: "Paste or type text and let AI draft talking points.",
      description: "Paste notes, a transcript, or anything you want to discuss. AI will extract talking points you can review before saving.",
      placeholder: "e.g., Paste a conversation transcript, appointment notes, or describe what you want to discuss...",
      draftButton: "Draft",
      progress: "Analyzing your text...",
      error: "AI drafting failed. Please try again.",
      tryAgain: "Try Again",
      reviewTitle: "Review Drafted Items",
      reviewDescription: "Accept or reject each item, then save.",
    },
    visitPrepAgent: {
      phase: {
        listening: "Processing your input...",
        grounding: "Checking your health records...",
        drafting: "Drafting agenda items...",
        awaiting_confirmation: "Ready for your review",
        saving: "Saving your visit...",
      },
    },
    // Voice input (shared across wizard and add-with-AI dialog)
    voice: {
      recording: "Recording {{duration}}...",
      transcribing: "Transcribing...",
      startRecording: "Start voice recording",
      stopRecording: "Stop recording",
      transcribingAudio: "Transcribing audio",
    },
  },
  fab: {
    logEvent: "New Event",
    newMedication: "New Medication",
  },
  new: {
    title: "New Event",
    success: "Event Created!",
    redirecting: "Redirecting to timeline...",
    submit: "Create Event",
    hideForm: "Hide form",
    successInline: "Event saved. Added to your history.",
    goBack: "Go back",
  },
  form: {
    selectEventType: "Select an event type",
    filterEventTypes: "Filter event types...",
    eventType: "Event Type",
    title: "Title",
    startDate: "Start Date",
    endDateOptional: "End Date (optional)",
    noEndDate: "No end date",
    clearEndDate: "Clear end date",
    confidenceLevel: "Confidence Level",
    confidenceHint: "How certain are you about this event's details?",
    notesOptional: "Notes (optional)",
    notesPlaceholder: "Add any additional details about this event...",
    cancel: "Cancel",
    saving: "Saving...",
    saveEvent: "Save Event",
    saveFailed: "Failed to save event",
    switchForm: {
      dateLabel: "Switch Date",
      fromLabel: "From (current medication)",
      fromPlaceholder: "Select active medication…",
      toLabel: "To (new medication)",
      toPlaceholder: "Search or type medication name…",
      noActiveMedications: "No active medications for this date",
      loadingMedications: "Loading medications…",
    },
    doseChangeForm: {
      dateLabel: "Change Date",
      medicationLabel: "Medication",
      medicationPlaceholder: "Select active medication…",
      newDoseLabel: "New Dose",
      doseValuePlaceholder: "e.g., 400",
      doseUnitPlaceholder: "e.g., mg",
      noActiveMedications: "No active medications for this date",
      loadingMedications: "Loading medications…",
    },
    startMedicationForm: {
      saveFailed: "Failed to start medication",
      nameLabel: "Medication name",
      namePlaceholder: "Search or type medication name…",
      doseLabel: "Dose (optional)",
      doseValuePlaceholder: "e.g., 200",
      doseUnitPlaceholder: "e.g., mg",
      routeLabel: "Route (optional)",
      routePlaceholder: "e.g., oral",
      startDateLabel: "Start date",
      showInTrends: "Show on chart",
    },
    adherenceNoteForm: {
      medicationLabel: "Related Medication (optional)",
      medicationPlaceholder: "Select active medication…",
      noActiveMedications: "No active medications for this date",
      loadingMedications: "Loading medications…",
      clearMedication: "Clear medication link",
    },
    travelForm: {
      locationLabel: "Location (optional)",
      locationPlaceholder: "Select a saved location…",
      useDefaultLocation: "Use default location",
      loadingLocations: "Loading locations…",
      noLocations: "No saved locations yet. Add one in Settings to attach it to travel.",
      addLocationLabel: "Add city for this trip",
      searchPlaceholder: "Start typing a city...",
      addLocationButton: "Add city",
      searchHint: "Select a city from the suggestion list.",
      searching: "Searching cities...",
      noMatches: "No city matches that search.",
      selectionRequired: "Pick a city from the suggestion list before adding.",
      searchError: "Could not add that city. Please try another search.",
      rateLimitNote: "Too many requests - please wait a moment.",
      locationHint: "If set, weather overlays use this location during the travel period.",
    },
    selectDoseUnit: "Select unit",
    validation: {
      eventTypeRequired: "Please select an event type",
      titleRequired: "Title is required",
      titleTooLong: "Title must be 100 characters or less",
      startDateFuture: "Start date cannot be more than 1 year in the future",
      endDateBeforeStart: "End date must be on or after start date",
      doseChangeRequired: "Please provide a valid dose value or unit",
    },
  },
  categories: {
    medication: "Medication",
    medicalIntervention: "Medical Intervention",
    healthStatus: "Health Status",
    lifestyleEnvironment: "Lifestyle & Environment",
    positiveMilestone: "Positive Milestone",
    other: "Other",
  },
  confidence: {
    certain: "Certain",
    probable: "Probable",
    possible: "Possible",
  },
  eventType: {
    medication_start: "Medication",
    medication_stop: "Medication Stopped",
    medication_dose_change: "Dose Change",
    medication_switch: "Medication Switch",
    medication_adherence_note: "Adherence Note",
    medication_taken: "Medication Taken",
    surgery_procedure: "Surgery/Procedure",
    injection: "Injection",
    therapy_session: "Therapy Session",
    medical_device: "Medical Device",
    injury: "Injury",
    illness: "Illness",
    flare: "Flare-up",
    symptom_change: "Symptom Change",
    activity_change: "Activity Change",
    diet_change: "Diet Change",
    sleep_change: "Sleep Change",
    stress_event: "Stress Event",
    weather_season: "Weather/Season",
    travel: "Travel",
    custom: "Custom Event",
    pain_improvement: "Pain Improvement",
    milestone: "Milestone",
    coping_success: "Coping Success",
    social_activity: "Social Activity",
    exercise_achievement: "Exercise Achievement",
  },
  eventTypeSuggestion: {
    medication_start: "Search or type medication name…",
    medication_dose_change: "e.g., Increased Gabapentin to 300mg",
    medication_switch: "e.g., Switched from Tramadol to Codeine",
    medication_adherence_note: "e.g., Missed doses due to travel",
    medication_taken: "e.g., Took Ibuprofen",
    surgery_procedure: "e.g., Knee arthroscopy",
    injection: "e.g., Cortisone injection in shoulder",
    therapy_session: "e.g., Started physical therapy",
    medical_device: "e.g., Started using TENS unit",
    injury: "e.g., Twisted ankle while walking",
    illness: "e.g., Caught a cold",
    flare: "e.g., Fibromyalgia flare-up",
    symptom_change: "e.g., New numbness in left hand",
    activity_change: "e.g., Started swimming 3x weekly",
    diet_change: "e.g., Eliminated gluten from diet",
    sleep_change: "e.g., Started using sleep apnea machine",
    stress_event: "e.g., Work deadline pressure",
    weather_season: "e.g., Cold front arrived",
    travel: "e.g., Long flight to Europe",
    custom: "Brief description of the event",
    pain_improvement: "e.g., First week below 4 average pain",
    milestone: "e.g., 30 days of consistent tracking",
    coping_success: "e.g., Meditation helped during flare",
    social_activity: "e.g., Attended friend's birthday party",
    exercise_achievement: "e.g., Walked 5km without stopping",
  },
  list: {
    searchPlaceholder: "Search events…",
    timelineTitle: "Event timeline",
    timelineDescription: "Scroll your past events and search by name, type, or date.",
    eventsPageDescription: "Create events and search your event history by name, type, or date.",
    timelineSearchPlaceholder: "Search past events (name, type, or date)…",
    noMatchingEvents: "No events match your search",
    clearSearch: "Clear search",
    emptyTitle: "No events found",
    emptyDescription:
      "Events help you track important changes that might affect your pain levels, like starting new medications or procedures.",
    createFirst: "Create your first event",
    filterByType: "Filter by type:",
    allEvents: "All events",
    errorLoading: "Failed to load events",
    unknownError: "An unknown error occurred",
    loading: "Loading...",
    loadMore: "Load more",
    loadingAriaLabel: "Loading events",
    eventsForMonth: "Events for {{month}}",
  },
  correlation: {
    // Shared labels
    painImpactAnalysis: "Pain Impact Analysis",
    analysisWindow: "Analysis window",
    windowDays_one: "{{count}} day",
    windowDays_other: "{{count}} days",
    before: "Before",
    after: "After",
    during: "During",
    change: "Change",
    impact: "Impact",
    scale: "0 – 10",
    avgPain: "avg pain",
    points: "points",
    best: "Best",
    notEnoughData: "Not enough data",
    notEnoughDataDefault:
      "Not enough data to show a pattern. Continue tracking to see trends.",
    partialData: "Partial data available",
    entries: "{{count}} entries",
    // Trend labels
    trendIncreasing: "Pain increased",
    trendDecreasing: "Pain decreased",
    trendStable: "Pain stable",
    trendInsufficient: "Not enough data",
    trendUnknown: "Unknown",
    // Multi-event comparison card
    eventComparison: "Event Comparison",
    viewMode: "View mode",
    columnView: "Column view",
    tableView: "Table view",
    metric: "Metric",
    eventN: "Event {{n}}",
    event: "Event",
    date: "Date",
    beforeAvg: "Before Avg",
    afterAvg: "After Avg",
    trend: "Trend",
    overallComparison: "Overall Comparison",
    eventsWithData: "Events with data:",
    withImprovement: "With improvement:",
    insufficientDataTitle: "Insufficient Data",
    noEventsHaveData:
      "None of the selected events have enough data for comparison.",
    onlySomeHaveData:
      "Only {{count}} of the selected events have enough data. Select events with more tracked data.",
    continueTracking: "Continue tracking to see event comparisons.",
    disclaimer:
      "This comparison shows observed patterns in your tracked data. Correlation does not imply causation. Discuss with your healthcare provider.",
    disclaimerSingle:
      "This is a descriptive summary of your tracked data, not medical advice. Discuss patterns with your healthcare provider.",
  },
  compare: {
    selectionTitle: "Select Events to Compare",
    comparisonTitle: "Event Comparison",
    selectionDescription:
      "Choose 2-3 events to compare their impact on your pain levels",
    comparisonDescription:
      "Compare how each event affected your pain patterns",
    backToSelection: "Back to selection",
    backToTimeline: "Back to timeline",
    logEvent: "Log Event",
    infoParagraph1:
      "Compare how different events affected your pain patterns. Select 2-3 events to see their before and after pain metrics side by side.",
    infoParagraph2:
      "For best results, choose events that are at least 2 weeks apart to avoid overlapping analysis windows.",
    compareDifferent: "Compare Different Events",
    filterPlaceholder: "Filter events by name or type…",
    noMatchingFilter: "No events match your filter",
    clearFilter: "Clear filter",
    eventsCount_one: "{{count}} event",
    eventsCount_other: "{{count}} events",
    selectedEvents: "Selected Events ({{count}})",
    availableEvents: "Available Events",
    availableEventsCount: "({{count}} from last 90 days)",
    loadingEvents: "Loading events...",
    noEventsFound: "No events found in the last 90 days.",
    noEventsHint: "Create some events to start comparing.",
    compareEvents: "Compare Events",
    clickCompareHint: "Click compare to see side-by-side analysis",
    selectPrompt: "Select 2-3 events to compare",
    selectMore_one: "Select {{count}} more event to compare",
    selectMore_other: "Select {{count}} more events to compare",
    readyToCompare: "Ready to compare {{count}} events",
    maxSelected: "Maximum {{max}} events selected",
    availableEventsAriaLabel: "Available events for selection",
    categoryEventsAriaLabel: "{{category}} events",
    selectAriaLabel: "Select {{title}}",
    deselectAriaLabel: "Deselect {{title}}",
    selectedChipAriaLabel: "Selected: {{title}}",
    removeFromSelectionAriaLabel: "Remove {{title}} from selection",
    compareAriaLabel: "Compare {{count}} events",
  },
  detail: {
    edit: "Edit",
    editTitle: "Edit Event",
    editDescription: "Update the event details below",
    notes: "Notes",
    confidenceLevel: "Confidence Level",
    created: "Created {{time}}",
    updated: "Updated {{time}}",
    deleteConfirmTitle: "Delete this event?",
    deleteConfirmDescription: "Are you sure you want to delete this event? This can be recovered within 30 days.",
    saveChanges: "Save Changes",
  },
} as const;

export const eventsFi = {
  tabs: {
    ariaLabel: "Päiväkirjanäkymä",
    events: "Tapahtumat",
    medications: "Lääkitykset",
    doctorVisits: "Lääkärikäynnit",
  },
  medications: {
    title: "Lääkitykset",
    intro: "Seuraa jatkuvia lääkityksiä, säädä annoksia ja kirjaa poikkeukset samassa paikassa.",
    placeholder: "Lääkityksesi näkyvät täällä. Tämä ominaisuus on tulossa pian.",
    addMedication: "Uusi lääkitys",
    // Medication card fields
    dose: "Annos",
    route: "Antoreitti",
    activeSince: "Aktiivinen {{date}} alkaen",
    stoppedOn: "Lopetettu {{date}}",
    activeStatus: "Aktiivinen",
    stoppedStatus: "Lopetettu",
    showInTrends: "Näytä trendit-sivun kaaviossa",
    regularInfo: "Täällä hallitaan säännölliset/jatkuvat lääkitykset. Kertaluonteiset kipulääkkeet voit kirjata päivittäisessä check-inissä.",
    regularInfoAria: "Näytä säännöllisten lääkkeiden ohje",
    noMedications: "Ei lääkityksiä",
    noMedicationsHint: "Aloittamasi lääkitykset näkyvät täällä.",
    loading: "Ladataan lääkityksiä…",
    errorLoading: "Lääkitysten lataus epäonnistui",
    retry: "Yritä uudelleen",
    // Card actions
    actions: {
      edit: "Muokkaa",
      switch: "Vaihda",
      stop: "Lopeta",
      logNote: "Kirjaa huomio",
      delete: "Poista",
    },
    // Edit dialog
    editDialog: {
      title: "Muokkaa lääkitystä",
      displayName: "Nimi",
      doseValue: "Annoksen arvo",
      doseValuePlaceholder: "esim. 200",
      doseUnit: "Annoksen yksikkö",
      doseUnitPlaceholder: "esim. mg",
      route: "Antoreitti",
      routePlaceholder: "esim. suun kautta",
      save: "Tallenna muutokset",
      saving: "Tallennetaan…",
      cancel: "Peruuta",
    },
    // Stop dialog
    stopDialog: {
      title: "Lopeta lääkitys",
      description: "Aseta päivämäärä, jolloin lääkitys lopetettiin.",
      dateLabel: "Lopetuspäivä",
      confirm: "Lopeta lääkitys",
      confirming: "Lopetetaan…",
      cancel: "Peruuta",
    },
    // Switch dialog
    switchDialog: {
      title: "Vaihda lääkitys",
      description: "Vaihda lääkkeestä {{from}} uuteen lääkkeeseen.",
      dateLabel: "Vaihtopäivä",
      toLabel: "Mihin (uusi lääke)",
      toPlaceholder: "Hae tai kirjoita lääkkeen nimi…",
      toRequired: "Anna uuden lääkkeen nimi",
      notesLabel: "Muistiinpanot (valinnainen)",
      notesPlaceholder: "Muistiinpanoja vaihdosta…",
      confirm: "Vaihda lääkitys",
      confirming: "Vaihdetaan…",
      cancel: "Peruuta",
    },
    // Adherence note
    noteDialog: {
      title: "Kirjaa huomio lääkityksestä",
      description: "Kirjaa huomio tämän lääkkeen ottamisesta.",
      dateLabel: "Päivämäärä",
      notePlaceholder: "esim. Aamuannos jäi väliin matkustamisen vuoksi",
      submit: "Tallenna huomio",
      submitting: "Tallennetaan…",
      cancel: "Peruuta",
    },
    // Delete dialog (T5.4)
    deleteDialog: {
      title: "Poista lääkitys",
      description: "Tämä piilottaa lääkkeen {{name}} kaikista lääkityslistoista. Historialliset tapahtumat säilytetään.",
      confirm: "Poista lääkitys",
      confirming: "Poistetaan…",
      cancel: "Peruuta",
    },
    // Add medication dialog
    addDialog: {
      title: "Lisää lääkitys",
      nameLabel: "Lääkkeen nimi",
      namePlaceholder: "Hae tai kirjoita lääkkeen nimi…",
      nameRequired: "Anna lääkkeen nimi",
      doseValueLabel: "Annoksen arvo",
      doseValuePlaceholder: "esim. 200",
      doseUnitLabel: "Annoksen yksikkö",
      doseUnitPlaceholder: "esim. mg",
      startDateLabel: "Aloituspäivä",
      startDateRequired: "Anna aloituspäivä",
      notesLabel: "Muistiinpanot (valinnainen)",
      notesPlaceholder: "Muistiinpanoja tästä lääkityksestä…",
      showInTrends: "Näytä trendit-sivun kaaviossa",
      save: "Lisää lääkitys",
      saving: "Lisätään…",
      cancel: "Peruuta",
    },
  },
  doctorVisits: {
    title: "Lääkärikäynnit",
    intro: "Suunnittele tulevat käynnit ja kirjaa puheenaiheet, kysymykset ja huolenaiheet jokaiselle vastaanotolle.",
    placeholder: "Suunnittele ja valmistaudu lääkärikäynteihin. Tulevat ja menneet käynnit näkyvät täällä.",
    // List view
    upcoming: "Tulevat",
    past: "Menneet",
    noVisits: "Ei lääkärikäyntejä",
    noVisitsHint: "Lisää käynti aloittaaksesi puheenaiheiden suunnittelun.",
    addVisit: "Uusi käynti",
    loading: "Ladataan käyntejä…",
    errorLoading: "Käyntien lataus epäonnistui",
    retry: "Yritä uudelleen",
    // Card
    itemCount_one: "{{count}} puheenaihe",
    itemCount_other: "{{count}} puheenaihetta",
    statusPlanned: "Suunniteltu",
    statusCompleted: "Toteutunut",
    statusCancelled: "Peruttu",
    // Detail view
    back: "Takaisin käynteihin",
    visitDate: "Päivämäärä",
    visitTime: "Aika",
    visitType: "Käyntityyppi",
    clinician: "Lääkäri",
    clinic: "Klinikka",
    status: "Tila",
    agendaNotes: "Muistiinpanot",
    talkingPoints: "Puheenaiheet",
    noItems: "Ei puheenaiheita",
    noItemsHint: "Lisää kysymyksiä, huolia tai aiheita, joita haluat käsitellä käynnillä.",
    addItem: "Lisää puheenaihe",
    // Item types
    itemType: {
      question: "Kysymys",
      concern: "Huolenaihe",
      symptom_change: "Oiremuutos",
      medication: "Lääkitys",
      event: "Tapahtuma",
      functional_impact: "Toimintakyvyn vaikutus",
      other: "Muu",
    },
    // Create visit dialog
    createTitle: "Uusi lääkärikäynti",
    createVisitDate: "Käyntipäivä",
    createVisitType: "Käyntityyppi",
    createVisitTime: "Aika (valinnainen)",
    createClinician: "Lääkäri (valinnainen)",
    createClinic: "Klinikka (valinnainen)",
    createSave: "Luo käynti",
    createSaving: "Luodaan…",
    createCancel: "Peruuta",
    // Add item dialog
    addItemTitle: "Lisää puheenaihe",
    addItemText: "Mitä haluat keskustella?",
    addItemTextPlaceholder: "esim. Kysy pitkävaikutteiseen lääkkeeseen vaihtamisesta…",
    addItemType: "Tyyppi",
    addItemSave: "Lisää",
    addItemSaving: "Lisätään…",
    addItemCancel: "Peruuta",
    // Visit types
    visitTypes: {
      general_checkup: "Yleistarkastus",
      specialist: "Erikoislääkäri",
      follow_up: "Seurantakäynti",
      urgent_care: "Päivystys",
      other: "Muu",
    },
    // Prepare packet wizard (T5.3)
    wizard: {
      title: "Valmistele käyntipaketti",
      close: "Sulje",
      back: "Takaisin",
      next: "Seuraava",
      stepOf: "Vaihe {{current}} / {{total}}",
      // Step 1: Your Words
      step1Title: "Omat sanat",
      step1Description: "Kirjoita vapaasti mitä haluat mainita käynnillä. Tämä on valinnainen — voit siirtyä eteenpäin.",
      notesPlaceholder: "esim. Aamuisin on ollut enemmän jäykkyyttä, varsinkin…",
      aiDraftButton: "Luonnostele tekoälyllä",
      aiComingSoon: "Tulossa pian",
      skipStep: "Ohita",
      // Step 2: Timeframe
      step2Title: "Aikajakso",
      step2Description: "Valitse yhteenvetotietojen aikajakso.",
      timeframePresets: {
        sinceLastVisit: "Edellisestä käynnistä",
        last2Weeks: "Viimeiset 2 viikkoa",
        last30Days: "Viimeiset 30 päivää",
        last90Days: "Viimeiset 90 päivää",
        custom: "Mukautettu",
      },
      timeframeBasis: {
        last_completed_visit_by_type_and_clinician: "Perustuu edelliseen käyntiin tämän lääkärin kanssa",
        last_completed_visit_by_type: "Perustuu edelliseen {{visitType}}-käyntiin",
        last_completed_visit_global: "Perustuu viimeisimpään toteutuneeseen käyntiin",
        default_30_days: "Oletus: viimeiset 30 päivää",
      },
      customStart: "Alkupäivä",
      customEnd: "Loppupäivä",
      // Step 3: Include & Preview
      step3Title: "Sisällytä ja esikatsele",
      step3Description: "Valitse mitä pakettiin sisällytetään ja tarkista kohokohdat.",
      sections: {
        painSummary: "Kipuyhteenveto",
        medications: "Lääkitykset",
        functionalImpact: "Toimintakyvyn vaikutus",
        keyEvents: "Avaintapahtumat",
        appendix: "Liite (päivittäiset tiedot)",
      },
      loadingContext: "Ladataan tietojasi…",
      errorContext: "Tietoja ei voitu ladata tälle aikajaksolle.",
      retryContext: "Yritä uudelleen",
      noData: "Ei tietoja tälle aikajaksolle.",
      painStatsLabel: "Kiputilastot",
      painMean: "Keskiarvo",
      painMedian: "Mediaani",
      painMin: "Alin",
      painMax: "Ylin",
      daysTracked_one: "{{count}} päivä seurattu",
      daysTracked_other: "{{count}} päivää seurattu",
      topLocations: "Yleisimmät sijainnit",
      topQualities: "Yleisimmät laadut",
      medicationChanges: "Lääkitysmuutokset",
      medicationChangeStarted: "Aloitettu",
      medicationChangeStopped: "Lopetettu",
      medicationChangeDose: "Annosmuutos",
      noMedicationChanges: "Ei lääkitysmuutoksia tällä jaksolla",
      functionalImpactAvg: "Keskimääräinen vaikutus",
      noFunctionalData: "Toimintakyvyn vaikutustietoja ei kirjattu",
      suggestedEvents: "Ehdotetut avaintapahtumat",
      noEvents: "Ei avaintapahtumia tällä jaksolla",
      // Step 4: Review
      step4Title: "Tarkista yhteenveto",
      step4Description: "Tarkista pakettisi ennen vientiä. Säädä yksityisyysasetuksia alla.",
      omitNotes: "Jätä henkilökohtaiset muistiinpanot pois",
      removePatientName: "Poista potilaan nimi",
      reviewSection: {
        painSummary: "Kipuyhteenveto",
        medications: "Lääkitykset",
        functionalImpact: "Toimintakyvyn vaikutus",
        keyEvents: "Avaintapahtumat",
      },
      // Step 5: Export
      step5Title: "Vie",
      step5Description: "Lataa pakettisi tai kopioi se tekstinä.",
      exportPdf: "Lataa PDF",
      exportText: "Lataa teksti",
      exporting: "Viedään…",
      exportSuccess: "Ladattu!",
      exportError: "Vienti epäonnistui. Yritä uudelleen.",
      // AI draft review (T4.2)
      aiProgress: "{{message}}",
      aiReviewTitle: "Tarkista tekoälyluonnos",
      aiReviewDescription: "Tarkista ehdotettu käynti ja puheenaiheet. Hyväksy, muokkaa tai hylkää kukin kohta.",
      aiVisitHeader: "Käynnin tiedot",
      aiAcceptAll: "Hyväksy kaikki",
      aiConfirm: "Tallenna hyväksytyt",
      aiConfirming: "Tallennetaan...",
      aiReject: "Hylkää",
      aiAccept: "Hyväksy",
      aiConfidence: "Luottamus: {{value}} %",
      aiSource: "Lähde: {{source}}",
      aiSourceLabel: {
        ai_draft: "Tekoälyluonnos",
        user: "Sinä",
      },
      aiSourceRefLabel: {
        event: "Tapahtumasta",
        daily_record: "Päivittäisestä kirjauksesta",
        medication: "Lääkityksestä",
        freeform: "Tekstistäsi",
      },
      aiNoItems: "Kohteita ei luonnosteltu.",
      aiError: "Tekoälyluonnostelu epäonnistui. Yritä uudelleen.",
      aiItemCount_one: "{{count}} kohde luonnosteltu",
      aiItemCount_other: "{{count}} kohdetta luonnosteltu",
    },
    preparePacket: "Valmistele paketti",
    // Add with AI dialog (T4.3)
    addWithAi: {
      button: "Lisää tekoälyllä",
      title: "Lisää tekoälyllä",
      subtitle: "Liitä tai kirjoita tekstiä ja anna tekoälyn luonnostella puheenaiheita.",
      description: "Liitä muistiinpanoja, puhelinmuistio tai kuvaile mitä haluat käsitellä. Tekoäly poimii puheenaiheet, jotka voit tarkistaa ennen tallentamista.",
      placeholder: "esim. Liitä keskustelumuistio, vastaanottomuistiinpanot tai kuvaile mitä haluat keskustella...",
      draftButton: "Luonnostele",
      progress: "Analysoidaan tekstiäsi...",
      error: "Tekoälyluonnostelu epäonnistui. Yritä uudelleen.",
      tryAgain: "Yritä uudelleen",
      reviewTitle: "Tarkista luonnostellut kohteet",
      reviewDescription: "Hyväksy tai hylkää kukin kohde ja tallenna sitten.",
    },
    visitPrepAgent: {
      phase: {
        listening: "Käsitellään syötettäsi...",
        grounding: "Tarkistetaan terveystietojasi...",
        drafting: "Luodaan asialistaa...",
        awaiting_confirmation: "Valmis tarkistettavaksi",
        saving: "Tallennetaan käyntiä...",
      },
    },
    // Voice input (shared across wizard and add-with-AI dialog)
    voice: {
      recording: "Nauhoitetaan {{duration}}...",
      transcribing: "Litteroidaan...",
      startRecording: "Aloita äänitallenne",
      stopRecording: "Lopeta tallennus",
      transcribingAudio: "Litteroidaan ääntä",
    },
  },
  fab: {
    logEvent: "Uusi tapahtuma",
    newMedication: "Uusi lääkitys",
  },
  new: {
    title: "Uusi tapahtuma",
    success: "Tapahtuma luotu!",
    redirecting: "Siirrytään aikajanalle...",
    submit: "Luo tapahtuma",
    hideForm: "Piilota lomake",
    successInline: "Tapahtuma tallennettu ja lisätty historiaan.",
    goBack: "Takaisin",
  },
  form: {
    selectEventType: "Valitse tapahtumatyyppi",
    filterEventTypes: "Suodata tapahtumatyyppejä...",
    eventType: "Tapahtumatyyppi",
    title: "Otsikko",
    startDate: "Aloituspäivä",
    endDateOptional: "Päättymispäivä (valinnainen)",
    noEndDate: "Ei päättymispäivää",
    clearEndDate: "Poista päättymispäivä",
    confidenceLevel: "Luottamustaso",
    confidenceHint: "Kuinka varma olet tämän tapahtuman tiedoista?",
    notesOptional: "Muistiinpanot (valinnainen)",
    notesPlaceholder: "Lisää tapahtumaan liittyviä lisätietoja...",
    cancel: "Peruuta",
    saving: "Tallennetaan...",
    saveEvent: "Tallenna tapahtuma",
    saveFailed: "Tapahtuman tallennus epäonnistui",
    switchForm: {
      dateLabel: "Vaihtopäivä",
      fromLabel: "Mistä (nykyinen lääke)",
      fromPlaceholder: "Valitse aktiivinen lääke…",
      toLabel: "Mihin (uusi lääke)",
      toPlaceholder: "Hae tai kirjoita lääkkeen nimi…",
      noActiveMedications: "Ei aktiivisia lääkityksiä tälle päivälle",
      loadingMedications: "Ladataan lääkityksiä…",
    },
    doseChangeForm: {
      dateLabel: "Muutospäivä",
      medicationLabel: "Lääke",
      medicationPlaceholder: "Valitse aktiivinen lääke…",
      newDoseLabel: "Uusi annos",
      doseValuePlaceholder: "esim. 400",
      doseUnitPlaceholder: "esim. mg",
      noActiveMedications: "Ei aktiivisia lääkityksiä tälle päivälle",
      loadingMedications: "Ladataan lääkityksiä…",
    },
    startMedicationForm: {
      saveFailed: "Lääkityksen aloitus epäonnistui",
      nameLabel: "Lääkkeen nimi",
      namePlaceholder: "Hae tai kirjoita lääkkeen nimi…",
      doseLabel: "Annos (valinnainen)",
      doseValuePlaceholder: "esim. 200",
      doseUnitPlaceholder: "esim. mg",
      routeLabel: "Antoreitti (valinnainen)",
      routePlaceholder: "esim. suun kautta",
      startDateLabel: "Aloituspäivä",
      showInTrends: "Näytä trendit-sivun kaaviossa",
    },
    adherenceNoteForm: {
      medicationLabel: "Liittyvä lääke (valinnainen)",
      medicationPlaceholder: "Valitse aktiivinen lääke…",
      noActiveMedications: "Ei aktiivisia lääkityksiä tälle päivälle",
      loadingMedications: "Ladataan lääkityksiä…",
      clearMedication: "Poista lääkelinkki",
    },
    travelForm: {
      locationLabel: "Sijainti (valinnainen)",
      locationPlaceholder: "Valitse tallennettu sijainti…",
      useDefaultLocation: "Käytä oletussijaintia",
      loadingLocations: "Ladataan sijainteja…",
      noLocations: "Ei tallennettuja sijainteja. Lisää sijainti Asetuksissa liittääksesi sen matkaan.",
      addLocationLabel: "Lisää kaupunki tätä matkaa varten",
      searchPlaceholder: "Kirjoita kaupungin nimi...",
      addLocationButton: "Lisää kaupunki",
      searchHint: "Valitse kaupunki ehdotuslistasta.",
      searching: "Haetaan kaupunkeja...",
      noMatches: "Hakua vastaavaa kaupunkia ei löytynyt.",
      selectionRequired: "Valitse kaupunki ehdotuslistasta ennen lisäämistä.",
      searchError: "Kaupungin lisääminen epäonnistui. Kokeile toista hakua.",
      rateLimitNote: "Liian monta pyyntöä - odota hetki.",
      locationHint: "Jos asetettu, trendien sääkaavio käyttää tätä sijaintia matkajakson aikana.",
    },
    selectDoseUnit: "Valitse yksikkö",
    validation: {
      eventTypeRequired: "Valitse tapahtumatyyppi",
      titleRequired: "Otsikko on pakollinen",
      titleTooLong: "Otsikko voi olla enintään 100 merkkiä",
      startDateFuture: "Aloituspäivä ei voi olla yli vuoden tulevaisuudessa",
      endDateBeforeStart: "Päättymispäivän on oltava aloituspäivänä tai sen jälkeen",
      doseChangeRequired: "Anna kelvollinen annoksen arvo tai yksikkö",
    },
  },
  categories: {
    medication: "Lääkitys",
    medicalIntervention: "Lääketieteellinen toimenpide",
    healthStatus: "Terveydentila",
    lifestyleEnvironment: "Elämäntapa ja ympäristö",
    positiveMilestone: "Positiivinen virstanpylväs",
    other: "Muu",
  },
  confidence: {
    certain: "Varma",
    probable: "Todennäköinen",
    possible: "Mahdollinen",
  },
  eventType: {
    medication_start: "Lääkitys",
    medication_stop: "Lääkitys lopetettu",
    medication_dose_change: "Annosmuutos",
    medication_switch: "Lääkityksen vaihto",
    medication_adherence_note: "Lääkityshuomio",
    medication_taken: "Lääke otettu",
    surgery_procedure: "Leikkaus/Toimenpide",
    injection: "Injektio",
    therapy_session: "Terapiakäynti",
    medical_device: "Lääkinnällinen laite",
    injury: "Vamma",
    illness: "Sairaus",
    flare: "Pahenemisvaihe",
    symptom_change: "Oiremuutos",
    activity_change: "Aktiivisuuden muutos",
    diet_change: "Ruokavalion muutos",
    sleep_change: "Unen muutos",
    stress_event: "Stressitapahtuma",
    weather_season: "Sää/Vuodenaika",
    travel: "Matkustaminen",
    custom: "Mukautettu tapahtuma",
    pain_improvement: "Kivun helpottuminen",
    milestone: "Virstanpylväs",
    coping_success: "Onnistunut hallintakeino",
    social_activity: "Sosiaalinen toiminta",
    exercise_achievement: "Liikuntasaavutus",
  },
  eventTypeSuggestion: {
    medication_start: "Hae tai kirjoita lääkkeen nimi…",
    medication_dose_change: "esim. Nostin Gabapentiinin annoksen 300 mg:aan",
    medication_switch: "esim. Vaihdoin Tramadolista Kodeiiniin",
    medication_adherence_note: "esim. Annoksia jäi väliin matkalla",
    medication_taken: "esim. Otin ibuprofeenia",
    surgery_procedure: "esim. Polven tähystys",
    injection: "esim. Kortisonipistos olkapäähän",
    therapy_session: "esim. Aloitin fysioterapian",
    medical_device: "esim. Aloitin TENS-laitteen käytön",
    injury: "esim. Nilkka nyrjähti kävellessä",
    illness: "esim. Sain flunssan",
    flare: "esim. Fibromyalgian pahenemisvaihe",
    symptom_change: "esim. Uutta puutumista vasemmassa kädessä",
    activity_change: "esim. Aloitin uimisen 3x viikossa",
    diet_change: "esim. Poistin gluteenin ruokavaliosta",
    sleep_change: "esim. Aloitin uniapnealaitteen käytön",
    stress_event: "esim. Työdeadline aiheutti painetta",
    weather_season: "esim. Kylmä rintama saapui",
    travel: "esim. Pitkä lento Eurooppaan",
    custom: "Lyhyt kuvaus tapahtumasta",
    pain_improvement: "esim. Ensimmäinen viikko alle 4 keskikivun",
    milestone: "esim. 30 päivää säännöllistä seurantaa",
    coping_success: "esim. Meditaatio auttoi pahenemisvaiheessa",
    social_activity: "esim. Osallistuin ystävän syntymäpäiville",
    exercise_achievement: "esim. Kävelin 5 km pysähtymättä",
  },
  list: {
    searchPlaceholder: "Hae tapahtumia…",
    timelineTitle: "Tapahtuma-aikajana",
    timelineDescription: "Selaa aiempia tapahtumia ja hae nimen, tyypin tai päivämäärän perusteella.",
    eventsPageDescription: "Luo tapahtumia ja hae tapahtumahistoriaasi nimen, tyypin tai päivämäärän perusteella.",
    timelineSearchPlaceholder: "Hae aiempia tapahtumia (nimi, tyyppi tai päivämäärä)…",
    noMatchingEvents: "Hakuasi vastaavia tapahtumia ei löytynyt",
    clearSearch: "Tyhjennä haku",
    emptyTitle: "Tapahtumia ei löytynyt",
    emptyDescription:
      "Tapahtumat auttavat seuraamaan merkittäviä muutoksia, jotka voivat vaikuttaa kipuihisi, kuten uusien lääkkeiden aloittaminen tai toimenpiteet.",
    createFirst: "Luo ensimmäinen tapahtuma",
    filterByType: "Suodata tyypillä:",
    allEvents: "Kaikki tapahtumat",
    errorLoading: "Tapahtumien lataus epäonnistui",
    unknownError: "Tuntematon virhe",
    loading: "Ladataan...",
    loadMore: "Lataa lisää",
    loadingAriaLabel: "Ladataan tapahtumia",
    eventsForMonth: "Tapahtumat: {{month}}",
  },
  correlation: {
    // Shared labels
    painImpactAnalysis: "Kipuvaikutusanalyysi",
    analysisWindow: "Analysointi-ikkuna",
    windowDays_one: "{{count}} päivä",
    windowDays_other: "{{count}} päivää",
    before: "Ennen",
    after: "Jälkeen",
    during: "Aikana",
    change: "Muutos",
    impact: "Vaikutus",
    scale: "0 – 10",
    avgPain: "keskikipu",
    points: "pistettä",
    best: "Paras",
    notEnoughData: "Ei tarpeeksi dataa",
    notEnoughDataDefault:
      "Ei tarpeeksi dataa kuvion näyttämiseen. Jatka seurantaa nähdäksesi trendejä.",
    partialData: "Osittaista dataa saatavilla",
    entries: "{{count}} kirjausta",
    // Trend labels
    trendIncreasing: "Kipu lisääntynyt",
    trendDecreasing: "Kipu vähentynyt",
    trendStable: "Kipu vakaa",
    trendInsufficient: "Ei tarpeeksi dataa",
    trendUnknown: "Tuntematon",
    // Multi-event comparison card
    eventComparison: "Tapahtumien vertailu",
    viewMode: "Näkymätila",
    columnView: "Sarakenäkymä",
    tableView: "Taulukkonäkymä",
    metric: "Mittari",
    eventN: "Tapahtuma {{n}}",
    event: "Tapahtuma",
    date: "Päivämäärä",
    beforeAvg: "Keskiarvo ennen",
    afterAvg: "Keskiarvo jälkeen",
    trend: "Trendi",
    overallComparison: "Kokonaisvertailu",
    eventsWithData: "Tapahtumat, joilla on dataa:",
    withImprovement: "Parantunut:",
    insufficientDataTitle: "Riittämätön data",
    noEventsHaveData:
      "Yhdelläkään valitulla tapahtumalla ei ole tarpeeksi dataa vertailuun.",
    onlySomeHaveData:
      "Vain {{count}} valituista tapahtumista on tarpeeksi dataa. Valitse tapahtumia, joilla on enemmän seurantatietoja.",
    continueTracking: "Jatka seurantaa nähdäksesi tapahtumien vertailuja.",
    disclaimer:
      "Tämä vertailu näyttää havaittuja kuvioita seurantatiedoissasi. Korrelaatio ei tarkoita syy-yhteyttä. Keskustele terveydenhuollon tarjoajasi kanssa.",
    disclaimerSingle:
      "Tämä on kuvaileva yhteenveto seurantatiedoistasi, ei lääketieteellinen neuvo. Keskustele kuvioista terveydenhuollon tarjoajasi kanssa.",
  },
  compare: {
    selectionTitle: "Valitse tapahtumat vertailtaviksi",
    comparisonTitle: "Tapahtumien vertailu",
    selectionDescription:
      "Valitse 2-3 tapahtumaa vertaillaksesi niiden vaikutusta kipuihisi",
    comparisonDescription:
      "Vertaa miten kukin tapahtuma vaikutti kipukuvioihisi",
    backToSelection: "Takaisin valintaan",
    backToTimeline: "Takaisin aikajanalle",
    logEvent: "Kirjaa tapahtuma",
    infoParagraph1:
      "Vertaa miten eri tapahtumat vaikuttivat kipukuvioihisi. Valitse 2-3 tapahtumaa nähdäksesi niiden ennen ja jälkeen -mittarit rinnakkain.",
    infoParagraph2:
      "Parhaan tuloksen saamiseksi valitse tapahtumia, joiden välillä on vähintään 2 viikkoa päällekkäisyyksien välttämiseksi.",
    compareDifferent: "Vertaa eri tapahtumia",
    filterPlaceholder: "Suodata tapahtumia nimen tai tyypin mukaan…",
    noMatchingFilter: "Suodatinta vastaavia tapahtumia ei löytynyt",
    clearFilter: "Tyhjennä suodatin",
    eventsCount_one: "{{count}} tapahtuma",
    eventsCount_other: "{{count}} tapahtumaa",
    selectedEvents: "Valitut tapahtumat ({{count}})",
    availableEvents: "Saatavilla olevat tapahtumat",
    availableEventsCount: "({{count}} viimeiseltä 90 päivältä)",
    loadingEvents: "Ladataan tapahtumia...",
    noEventsFound: "Tapahtumia ei löytynyt viimeiseltä 90 päivältä.",
    noEventsHint: "Luo tapahtumia aloittaaksesi vertailun.",
    compareEvents: "Vertaa tapahtumia",
    clickCompareHint: "Paina vertaile nähdäksesi rinnakkaisanalyysin",
    selectPrompt: "Valitse 2-3 tapahtumaa vertailtaviksi",
    selectMore_one: "Valitse vielä {{count}} tapahtuma vertailua varten",
    selectMore_other: "Valitse vielä {{count}} tapahtumaa vertailua varten",
    readyToCompare: "Valmis vertailemaan {{count}} tapahtumaa",
    maxSelected: "Enintään {{max}} tapahtumaa valittu",
    availableEventsAriaLabel: "Valittavissa olevat tapahtumat",
    categoryEventsAriaLabel: "{{category}} tapahtumat",
    selectAriaLabel: "Valitse {{title}}",
    deselectAriaLabel: "Poista valinta {{title}}",
    selectedChipAriaLabel: "Valittu: {{title}}",
    removeFromSelectionAriaLabel: "Poista {{title}} valinnasta",
    compareAriaLabel: "Vertaa {{count}} tapahtumaa",
  },
  detail: {
    edit: "Muokkaa",
    editTitle: "Muokkaa tapahtumaa",
    editDescription: "Päivitä tapahtuman tiedot alla",
    notes: "Muistiinpanot",
    confidenceLevel: "Luottamustaso",
    created: "Luotu {{time}}",
    updated: "Päivitetty {{time}}",
    deleteConfirmTitle: "Poistetaanko tämä tapahtuma?",
    deleteConfirmDescription: "Haluatko varmasti poistaa tämän tapahtuman? Se voidaan palauttaa 30 päivän kuluessa.",
    saveChanges: "Tallenna muutokset",
  },
} as const;
